// ---------------------------------------------------------------------------
// DiCharts SDK – CSS Styles (injected once)
// ---------------------------------------------------------------------------

const CSS = `
/* DiCharts Widget – Scoped Styles */
.dw-root {
  --dw-bg-primary: #0d1117;
  --dw-bg-secondary: #161b22;
  --dw-bg-tertiary: #1e222d;
  --dw-border: #30363d;
  --dw-text-primary: #e6edf3;
  --dw-text-secondary: #8b949e;
  --dw-accent: #2962ff;
  --dw-green: #26a69a;
  --dw-red: #ef5350;

  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: var(--dw-bg-primary);
  color: var(--dw-text-primary);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  overflow: hidden;
  position: relative;
  box-sizing: border-box;
}
.dw-root *, .dw-root *::before, .dw-root *::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.dw-root.dw-light {
  --dw-bg-primary: #ffffff;
  --dw-bg-secondary: #f0f3fa;
  --dw-bg-tertiary: #e8ecf1;
  --dw-border: #d1d5db;
  --dw-text-primary: #131722;
  --dw-text-secondary: #787b86;
}

/* ===== Toolbar ===== */
.dw-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 12px;
  background: var(--dw-bg-secondary);
  border-bottom: 1px solid var(--dw-border);
  flex-shrink: 0;
  gap: 4px;
}
.dw-toolbar-left, .dw-toolbar-right {
  display: flex;
  align-items: center;
  gap: 6px;
}

.dw-sep {
  width: 1px;
  height: 20px;
  background: var(--dw-border);
  flex-shrink: 0;
}

/* ===== Buttons ===== */
.dw-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 12px;
  background: var(--dw-bg-tertiary);
  color: var(--dw-text-secondary);
  border: 1px solid var(--dw-border);
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-family: inherit;
  transition: all 0.15s;
  white-space: nowrap;
  line-height: 1.4;
}
.dw-btn:hover { color: var(--dw-text-primary); }
.dw-btn.dw-active { color: var(--dw-green); border-color: var(--dw-green); }
.dw-btn .dw-chevron { opacity: 0.5; flex-shrink: 0; transition: opacity 0.15s; }
.dw-btn:hover .dw-chevron { opacity: 1; }

.dw-btn-icon { justify-content: center; padding: 4px 8px; }

/* ===== Symbol & Price ===== */
.dw-symbol-group {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 4px;
}
.dw-symbol-name {
  font-size: 13px;
  font-weight: 700;
  color: var(--dw-text-primary);
  white-space: nowrap;
}
.dw-symbol-price {
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  min-width: 7ch;
  text-align: right;
  transition: color 0.2s;
}
.dw-symbol-price.dw-up { color: var(--dw-green); }
.dw-symbol-price.dw-down { color: var(--dw-red); }

/* ===== Timeframe buttons ===== */
.dw-tf-group {
  display: flex;
  align-items: center;
  gap: 1px;
  background: var(--dw-bg-primary);
  border: 1px solid var(--dw-border);
  border-radius: 4px;
  padding: 1px;
}
.dw-tf-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3px 8px;
  background: none;
  border: none;
  border-radius: 3px;
  color: var(--dw-text-secondary);
  font-size: 11px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.12s;
  white-space: nowrap;
  line-height: 1.4;
}
.dw-tf-btn:hover { color: var(--dw-text-primary); background: var(--dw-bg-tertiary); }
.dw-tf-btn.dw-active { color: var(--dw-text-primary); background: var(--dw-accent); }

/* ===== Popup (shared) ===== */
.dw-popup-wrap { position: relative; }
.dw-popup {
  display: none;
  position: absolute;
  top: calc(100% + 8px);
  z-index: 1000;
  background: var(--dw-bg-secondary);
  border: 1px solid var(--dw-border);
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.4);
}
.dw-popup.dw-open { display: block; }
.dw-popup.dw-left { left: 0; }
.dw-popup.dw-right { right: 0; }

.dw-popup-title {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--dw-text-secondary);
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--dw-border);
}

/* ===== Style picker ===== */
.dw-style-opts { display: flex; flex-direction: column; gap: 2px; min-width: 170px; }
.dw-style-opt {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 8px;
  background: none;
  border: none;
  border-radius: 4px;
  color: var(--dw-text-secondary);
  cursor: pointer;
  font-size: 12px;
  font-family: inherit;
  transition: all 0.12s;
}
.dw-style-opt:hover { background: var(--dw-bg-tertiary); color: var(--dw-text-primary); }
.dw-style-opt.dw-active { color: var(--dw-green); background: rgba(38,166,154,0.08); }

/* ===== Indicator popup ===== */
.dw-ind-popup {
  width: 280px;
  padding: 0;
  overflow: hidden;
}
.dw-ind-popup.dw-open { display: flex; flex-direction: column; }

.dw-ind-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px 8px;
  border-bottom: 1px solid var(--dw-border);
}
.dw-ind-search {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  padding: 4px 8px;
  background: var(--dw-bg-primary);
  border: 1px solid var(--dw-border);
  border-radius: 4px;
}
.dw-ind-search:focus-within { border-color: var(--dw-accent); }
.dw-ind-search svg { color: var(--dw-text-secondary); flex-shrink: 0; }
.dw-ind-search input {
  flex: 1;
  background: none;
  border: none;
  outline: none;
  color: var(--dw-text-primary);
  font-size: 11px;
  font-family: inherit;
  min-width: 0;
}
.dw-ind-search input::placeholder { color: var(--dw-text-secondary); }

.dw-ind-tabs {
  display: flex;
  border-bottom: 1px solid var(--dw-border);
}
.dw-ind-tab {
  flex: 1;
  padding: 6px 0;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  color: var(--dw-text-secondary);
  font-size: 11px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.12s;
}
.dw-ind-tab:hover { color: var(--dw-text-primary); }
.dw-ind-tab.dw-active { color: var(--dw-green); border-bottom-color: var(--dw-green); font-weight: 600; }

.dw-ind-list {
  max-height: 320px;
  overflow-y: auto;
}
.dw-ind-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 7px 12px;
  background: none;
  border: none;
  border-bottom: 1px solid var(--dw-border);
  color: var(--dw-text-primary);
  font-size: 12px;
  font-family: inherit;
  cursor: pointer;
  text-align: left;
  transition: background 0.1s;
}
.dw-ind-row:last-child { border-bottom: none; }
.dw-ind-row:hover { background: var(--dw-bg-tertiary); }
.dw-ind-row.dw-active { background: rgba(38,166,154,0.06); }
.dw-ind-row.dw-hidden { display: none; }

.dw-ind-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
.dw-ind-name { flex: 1; font-weight: 600; }
.dw-ind-desc { font-weight: 400; color: var(--dw-text-secondary); margin-left: 2px; font-size: 11px; }
.dw-ind-star { font-size: 14px; color: var(--dw-border); transition: color 0.12s; }
.dw-ind-row.dw-active .dw-ind-star { color: var(--dw-green); }

/* ===== Settings popup ===== */
.dw-settings { min-width: 170px; }
.dw-settings-section { margin-bottom: 10px; }
.dw-settings-section:last-child { margin-bottom: 0; }
.dw-settings-label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--dw-text-secondary);
  margin-bottom: 6px;
}
.dw-check {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--dw-text-primary);
  cursor: pointer;
  padding: 3px 0;
  transition: color 0.15s;
}
.dw-check:hover { color: #fff; }
.dw-check input[type="checkbox"] {
  accent-color: var(--dw-accent);
  width: 13px;
  height: 13px;
  cursor: pointer;
}
.dw-color-dot {
  width: 7px; height: 7px;
  border-radius: 50%;
  margin-left: auto;
  flex-shrink: 0;
}

/* ===== Layout picker ===== */
.dw-layout-opts { display: flex; flex-direction: column; gap: 2px; min-width: 170px; }
.dw-layout-opt {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 8px;
  background: none;
  border: none;
  border-radius: 4px;
  color: var(--dw-text-secondary);
  cursor: pointer;
  font-size: 12px;
  font-family: inherit;
  transition: all 0.12s;
}
.dw-layout-opt:hover { background: var(--dw-bg-tertiary); color: var(--dw-text-primary); }
.dw-layout-opt.dw-active { color: var(--dw-accent); }
.dw-layout-opt.dw-active .dw-lo-cell { background: var(--dw-accent); opacity: 1; }

.dw-lo-icon {
  width: 28px;
  height: 20px;
  display: grid;
  gap: 2px;
  grid-template: 1fr / 1fr;
  flex-shrink: 0;
}
.dw-lo-icon.dw-lo-2v { grid-template: 1fr / 1fr 1fr; }
.dw-lo-icon.dw-lo-2h { grid-template: 1fr 1fr / 1fr; }
.dw-lo-icon.dw-lo-1-2r { grid-template: 1fr 1fr / 2fr 1fr; }
.dw-lo-icon.dw-lo-4 { grid-template: 1fr 1fr / 1fr 1fr; }
.dw-lo-cell { background: var(--dw-text-secondary); border-radius: 1px; opacity: 0.4; }

/* ===== Chart area (grid layouts) ===== */
.dw-chart-area {
  flex: 1;
  display: grid;
  grid-template: 1fr / 1fr;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  gap: 1px;
  background: var(--dw-border);
}
.dw-chart-area[data-layout="single"] { grid-template: 1fr / 1fr; }
.dw-chart-area[data-layout="2v"]     { grid-template: 1fr / 1fr 1fr; }
.dw-chart-area[data-layout="2h"]     { grid-template: 1fr 1fr / 1fr; }
.dw-chart-area[data-layout="1-2r"]   { grid-template: 1fr 1fr / 2fr 1fr; }
.dw-chart-area[data-layout="1-2r"] > :first-child { grid-row: 1 / 3; }
.dw-chart-area[data-layout="4grid"]  { grid-template: 1fr 1fr / 1fr 1fr; }

/* Chart pane (each grid cell) */
.dw-pane {
  position: relative;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  background: var(--dw-bg-primary);
}

/* Pane timeframe badge */
.dw-pane-badge {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 50;
  background: rgba(41, 98, 255, 0.85);
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 3px;
  pointer-events: none;
  letter-spacing: 0.03em;
}

/* ===== Chart navigation ===== */
.dw-chart-nav {
  position: absolute;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 60;
  display: flex;
  gap: 2px;
  background: var(--dw-bg-secondary);
  border: 1px solid var(--dw-border);
  border-radius: 6px;
  padding: 3px;
  opacity: 0;
  transition: opacity 0.2s;
  pointer-events: none;
}
.dw-pane:hover .dw-chart-nav {
  opacity: 1;
  pointer-events: auto;
}
.dw-nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 26px;
  background: none;
  border: none;
  border-radius: 4px;
  color: var(--dw-text-secondary);
  cursor: pointer;
  transition: all 0.12s;
}
.dw-nav-btn:hover {
  background: var(--dw-bg-tertiary);
  color: var(--dw-text-primary);
}

/* Sub-pane shrink */
.dw-root .dicharts-subpane { flex-shrink: 0; }
`;

let injected = false;

export function injectStyles() {
  if (injected) return;
  injected = true;
  const style = document.createElement('style');
  style.id = 'dicharts-sdk-styles';
  style.textContent = CSS;
  document.head.appendChild(style);
}
